import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  ChevronDown,
  ChevronUp,
  CheckCheck,
  CircleAlert,
  Clock3,
  FileText,
  Mail,
  MessageCircle,
  Paperclip,
  PencilLine,
  PhoneCall,
  RefreshCw,
  Search,
  SendHorizontal,
  UserRound,
  X
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { alpha, styled, useTheme } from '@mui/material/styles';

const useQuery = () => {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
};

const CUSTOMER_SEARCH_MIN_LENGTH = 3;
const ATTACHMENT_MAX_SIZE = 25 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'svg',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'csv',
  'mp3',
  'wav',
  'ogg',
  'mp4',
  'mov',
  'avi',
  'mkv'
]);

const parseJsonSafe = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return {};
  }
};

const extractMediaItemsFromPayload = (value) => {
  const payload = parseJsonSafe(value);
  const message = payload?.message || payload?.data || payload || {};
  const mediaSets = [
    ...(Array.isArray(message?.mediamessage_set) ? message.mediamessage_set : []),
    ...(Array.isArray(message?.media_set) ? message.media_set : []),
    ...(Array.isArray(message?.attachments) ? message.attachments : [])
  ];

  return mediaSets
    .map((item) => ({
      url: item?.media || item?.url || item?.download_url || item?.href || '',
      title: item?.title || item?.filename || item?.name || '',
      contentType: item?.content_type || item?.mime_type || '',
      raw: item
    }))
    .filter((item) => item.url);
};

const isImageMediaItem = (item = {}) => String(item?.contentType || '').toLowerCase().startsWith('image/');
const isPdfMediaItem = (item = {}) => String(item?.contentType || '').toLowerCase().includes('pdf');
const isDocumentMediaItem = (item = {}) => Boolean(item?.url) && !isImageMediaItem(item);
const getMediaDisplayTitle = (item = {}) => {
  if (isPdfMediaItem(item)) {
    return 'PDF';
  }
  if (isImageMediaItem(item)) {
    return 'Immagine ricevuta';
  }
  return 'Documento';
};

const getFileExtension = (name = '') => {
  const cleanName = String(name || '').trim();
  const dotIndex = cleanName.lastIndexOf('.');
  return dotIndex >= 0 ? cleanName.slice(dotIndex + 1).toLowerCase() : '';
};

const isAllowedAttachmentFile = (file) => {
  if (!file) return false;
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('image/') || mime.startsWith('video/') || mime.startsWith('audio/')) {
    return true;
  }
  return ALLOWED_ATTACHMENT_EXTENSIONS.has(getFileExtension(file.name));
};

const formatFileSize = (bytes) => {
  const value = Number(bytes || 0);
  if (!value) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / Math.pow(1024, index);
  return `${scaled >= 10 || index === 0 ? scaled.toFixed(0) : scaled.toFixed(1)} ${units[index]}`;
};

const getAttachmentSummaryLabel = (file) => {
  const mime = String(file?.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'Immagine';
  if (mime.startsWith('video/')) return 'Video';
  if (mime.startsWith('audio/')) return 'Audio';
  if (mime.includes('pdf')) return 'PDF';
  return 'Documento';
};

const languageLabel = (code) => {
  if (!code) return '';
  const c = String(code).toLowerCase();
  if (c.startsWith('it')) return 'Italiano';
  if (c.startsWith('en')) return 'Inglese';
  if (c.startsWith('es')) return 'Spagnolo';
  if (c.startsWith('de')) return 'Tedesco';
  if (c.startsWith('fr')) return 'Francese';
  if (c.startsWith('pt')) return c.includes('br') ? 'Portoghese (BR)' : 'Portoghese';
  return code.toUpperCase();
};

const normalizeTemplateStatus = (value) => {
  if (!value) return '';
  return String(value).trim().toLowerCase();
};

const getTemplateStatusLabel = (value) => {
  const normalized = normalizeTemplateStatus(value);
  if (!normalized) {
    return '';
  }
  if (normalized === 'approved') {
    return 'Approvato';
  }
  if (normalized === 'active') {
    return 'Attivo';
  }
  if (normalized === 'rejected') {
    return 'Rifiutato';
  }
  if (normalized === 'paused') {
    return 'In pausa';
  }
  if (normalized === 'pending') {
    return 'In attesa';
  }
  return String(value);
};

const deriveTemplateStatus = (template = {}) => {
  const meta = parseJsonSafe(template.metadata);
  const details = meta.details || meta || {};
  const directStatus = normalizeTemplateStatus(template.status || details.status || meta.status);
  if (directStatus) {
    return directStatus;
  }

  const rawLocalizations = [
    ...(Array.isArray(details.templatelocalization_set) ? details.templatelocalization_set : []),
    ...(Array.isArray(meta.templatelocalization_set) ? meta.templatelocalization_set : []),
    ...(Array.isArray(details.localizations) ? details.localizations : []),
    ...(Array.isArray(meta.localizations) ? meta.localizations : [])
  ];
  const uniqueStatuses = Array.from(
    new Set(rawLocalizations.map((item) => normalizeTemplateStatus(item?.status)).filter(Boolean))
  );

  if (uniqueStatuses.includes('approved')) {
    return 'approved';
  }
  if (uniqueStatuses.length === 1) {
    return uniqueStatuses[0];
  }
  if (typeof meta.is_approved === 'boolean' && meta.is_approved) {
    return 'approved';
  }
  return uniqueStatuses[0] || '';
};

const extractTemplatePlaceholders = (value) =>
  Array.from(new Set(String(value || '').match(/%%([A-Z0-9_]+)%%/gi) || []))
    .map((item) => item.replace(/%/g, ''))
    .filter(Boolean);

const renderTemplatePreview = (value, fields = {}) =>
  String(value || '').replace(/%%([A-Z0-9_]+)%%/gi, (match, key) => {
    const fieldValue = fields[key];
    return fieldValue === undefined || fieldValue === null || fieldValue === '' ? match : String(fieldValue);
  });

const buildTemplateFieldDefaults = (localization, options = {}) => {
  const { fallbackMessage = '', recipientFirstName = '' } = options;
  const bodyText = localization?.body_text || '';
  const rawCustomFields = parseJsonSafe(localization?.custom_fields);
  const placeholderKeys = extractTemplatePlaceholders(bodyText);
  const initialFields = {};

  placeholderKeys.forEach((key) => {
    initialFields[key] = '';
  });

  Object.entries(rawCustomFields).forEach(([key, value]) => {
    initialFields[key] = value ?? '';
  });

  if (Object.prototype.hasOwnProperty.call(initialFields, 'SENTENCE') && fallbackMessage) {
    initialFields.SENTENCE = fallbackMessage;
  }

  if (Object.prototype.hasOwnProperty.call(initialFields, 'FIRST_NAME') && recipientFirstName) {
    initialFields.FIRST_NAME = recipientFirstName;
  }

  return initialFields;
};

const hasActiveFreeTextWindow = (items = []) =>
  items.some((message) => {
    if (message?.direction !== 'inbound' || !message?.sent_at) {
      return false;
    }
    const sentAt = new Date(message.sent_at);
    if (Number.isNaN(sentAt.getTime())) {
      return false;
    }
    return Date.now() - sentAt.getTime() <= 24 * 60 * 60 * 1000;
  });

const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderHighlightedText = (value, queryText) => {
  const text = String(value || '');
  const query = String(queryText || '').trim();

  if (!text || !query || query.length < CUSTOMER_SEARCH_MIN_LENGTH) {
    return text;
  }

  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'ig'));
  return parts.map((part, index) => {
    if (part.toLowerCase() === query.toLowerCase()) {
      return (
        <Box
          key={`${part}-${index}`}
          component="mark"
          sx={{
            bgcolor: '#fde68a',
            color: 'inherit',
            px: 0.25,
            borderRadius: 0.75
          }}
        >
          {part}
        </Box>
      );
    }
    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

export default function SpokiChatPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const query = useQuery();
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const previousConversationIdRef = useRef(null);
  const previousMessagesCountRef = useRef(0);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filters, setFilters] = useState({
    phone: query.get('phone') || '',
    customer_id: query.get('customer_id') || '',
    status: ''
  });
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerLookup, setCustomerLookup] = useState([]);
  const [conversationCustomers, setConversationCustomers] = useState({});
  const [loadingCustomerLookup, setLoadingCustomerLookup] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [composerExpanded, setComposerExpanded] = useState(false);
  const [leftSidebarTab, setLeftSidebarTab] = useState('conversations');
  const [conversationTab, setConversationTab] = useState('all');
  const [conversationSearch, setConversationSearch] = useState('');
  const [templateOptions, setTemplateOptions] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingTemplateDetail, setLoadingTemplateDetail] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplateDetail, setSelectedTemplateDetail] = useState(null);
  const [selectedTemplateLanguage, setSelectedTemplateLanguage] = useState('');
  const [templateFields, setTemplateFields] = useState({});
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [showNewMessagesCta, setShowNewMessagesCta] = useState(false);
  const ui = useMemo(
    () => ({
      surface: isDark ? '#0f172a' : '#ffffff',
      surfaceAlt: isDark ? '#111827' : '#f6f8fb',
      surfaceMuted: isDark ? '#172033' : '#eef2f5',
      panelBorder: isDark
        ? '1px solid rgba(148, 163, 184, 0.20)'
        : '1px solid rgba(100, 116, 139, 0.22)',
      panelBorderSoft: isDark
        ? '1px solid rgba(148, 163, 184, 0.14)'
        : '1px solid rgba(100, 116, 139, 0.14)',
      panelBorderStrong: isDark
        ? '1px solid rgba(129, 140, 248, 0.32)'
        : '1px solid rgba(79, 70, 229, 0.26)',
      tabBg: isDark ? '#1e293b' : '#f1f5f9',
      tabSelectedBlue: isDark ? alpha(theme.palette.primary.main, 0.26) : '#e0e7ff',
      tabSelectedGreen: isDark ? alpha(theme.palette.success.main, 0.22) : '#e7f5ec',
      hoverBg: isDark ? '#162235' : '#f7f9fc',
      selectedBg: isDark ? '#12281f' : '#eef6f0',
      selectedHoverBg: isDark ? '#173125' : '#e5f1e8',
      selectedBorder: isDark ? '#4ade80' : '#9fd8af',
      messageAreaBg: isDark ? '#0b141a' : '#efeae2',
      messageAreaPattern: isDark
        ? `radial-gradient(circle at 24px 24px, rgba(255,255,255,0.04) 2px, transparent 2.5px),
           radial-gradient(circle at 72px 72px, rgba(255,255,255,0.03) 2px, transparent 2.5px),
           linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 48%)`
        : `radial-gradient(circle at 24px 24px, rgba(255,255,255,0.34) 2px, transparent 2.5px),
           radial-gradient(circle at 72px 72px, rgba(255,255,255,0.22) 2px, transparent 2.5px),
           linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 48%)`,
      messageAreaPatternSize: '96px 96px, 96px 96px, 100% 100%',
      inboundBubbleBg: isDark ? '#202c33' : '#ffffff',
      inboundBubbleText: isDark ? '#e9edef' : '#111b21',
      outboundBubbleBg: isDark ? '#005c4b' : '#d9fdd3',
      outboundBubbleText: isDark ? '#e9edef' : '#111b21',
      bubbleMetaText: isDark ? '#8696a0' : '#667781',
      bubbleShadow: isDark
        ? '0 1px 1px rgba(0, 0, 0, 0.28)'
        : '0 1px 0.5px rgba(11, 20, 26, 0.12)',
      composerDockBg: isDark ? '#111827' : '#f4f6f8',
      composerPanelBg: isDark ? '#0f172a' : '#ffffff',
      composerHeaderBg: isDark ? '#172033' : '#f5f7fb',
      iconBg: isDark ? alpha(theme.palette.primary.main, 0.18) : '#eef2ff',
      iconColor: isDark ? '#a5b4fc' : '#4f46e5',
      accentSoftBg: isDark ? '#1e293b' : '#edf2ff',
      avatarBg: isDark ? '#334155' : '#b8c4d5',
      metaIcon: isDark ? '#94a3b8' : '#64748b',
      statusOpenBg: isDark ? '#166534' : '#dcfce7',
      statusClosedBg: isDark ? '#334155' : '#f1f5f9',
      statusOtherBg: isDark ? '#854d0e' : '#fef3c7',
      statusText: isDark ? '#f8fafc' : '#0f172a',
      radiusPanel: 1,
      radiusCard: 0.75,
      radiusSoft: 0.75,
      radiusPill: 0.75
    }),
    [isDark, theme]
  );
  const trimmedCustomerSearch = customerSearch.trim();
  const customerSearchTooShort =
    trimmedCustomerSearch.length > 0 &&
    trimmedCustomerSearch.length < CUSTOMER_SEARCH_MIN_LENGTH;

  const scrollMessagesToBottom = (behavior = 'auto') => {
    requestAnimationFrame(() => {
      if (messagesEndRef.current?.scrollIntoView) {
        messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
        return;
      }
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior
        });
      }
    });
  };

  const isMessagesNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) {
      return true;
    }
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    return distanceFromBottom <= 96;
  };

  const handleMessagesScroll = () => {
    if (isMessagesNearBottom()) {
      setShowNewMessagesCta(false);
    }
  };

  const loadConversations = async (page = 1, activeFilters = filters) => {
    try {
      setLoadingConversations(true);
      const params = { page };
      if (activeFilters.phone) params.phone = activeFilters.phone;
      if (activeFilters.customer_id) params.customer_id = activeFilters.customer_id;
      if (activeFilters.status) params.status = activeFilters.status;
      const res = await api.get('/spoki/chat/conversations', { params });
      const data = res.data || {};
      const items = data.data || [];
      const nextMeta = data.meta || { page: 1, totalPages: 1, total: 0 };
      setConversations(items);
      setMeta(nextMeta);
      loadConversationCustomers(items);

      const nextSelected =
        items.find((item) => item.id === selectedConversation?.id) || items[0] || null;

      setSelectedConversation(nextSelected);

      if (nextSelected) {
        loadMessages(nextSelected);
      } else {
        setMessages([]);
      }
    } catch (error) {
      toast.error('Errore nel caricamento delle conversazioni Spoki');
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadMessages = async (conversation) => {
    try {
      setLoadingMessages(true);
      if (!conversation) {
        setMessages([]);
        return [];
      }
      const res = await api.get(`/spoki/chat/conversations/${conversation.id}/messages`);
      const items = res.data || [];
      setMessages(items);
      return items;
    } catch (error) {
      toast.error('Errore nel caricamento dei messaggi conversazione');
      return [];
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadCustomerLookup = async (search = customerSearch) => {
    const normalizedSearch = String(search || '').trim();

    if (normalizedSearch && normalizedSearch.length < CUSTOMER_SEARCH_MIN_LENGTH) {
      setCustomerLookup([]);
      setLoadingCustomerLookup(false);
      return;
    }

    try {
      setLoadingCustomerLookup(true);
      const params = { limit: 20 };
      if (normalizedSearch) {
        params.search = normalizedSearch;
      }
      const res = await api.get('/customer/lookup/chat', { params });
      const items = Array.isArray(res.data) ? res.data : [];
      setCustomerLookup(items);

      if (!selectedCustomer && query.get('customer_id')) {
        const preselected = items.find((item) => String(item.id) === query.get('customer_id'));
        if (preselected) {
          setSelectedCustomer(preselected);
        }
      }
    } catch (error) {
      toast.error('Errore nel caricamento della rubrica clienti');
    } finally {
      setLoadingCustomerLookup(false);
    }
  };

  const loadConversationCustomers = async (conversationItems = []) => {
    const ids = Array.from(
      new Set(
        conversationItems
          .map((item) => item.customer_id)
          .filter((id) => Number.isInteger(Number(id)) && Number(id) > 0)
          .map((id) => String(id))
      )
    );

    if (!ids.length) {
      return;
    }

    try {
      const res = await api.get('/customer/lookup/chat', {
        params: { ids: ids.join(',') }
      });
      const items = Array.isArray(res.data) ? res.data : [];
      setConversationCustomers((prev) => {
        const next = { ...prev };
        items.forEach((item) => {
          next[String(item.id)] = item;
        });
        return next;
      });
    } catch (error) {
      toast.error('Errore nel caricamento dei nominativi conversazioni');
    }
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const res = await api.get('/spoki/templates', {
        params: {
          page: 1,
          limit: 100,
          orderBy: 'name',
          orderDirection: 'asc'
        }
      });
      const result = res.data || {};
      const items = Array.isArray(result) ? result : result.data || [];
      setTemplateOptions(items);
      setSelectedTemplateId((prev) => {
        if (prev) {
          return prev;
        }
        const approvedItems = items.filter((item) => deriveTemplateStatus(item) === 'approved');
        const preferred =
          approvedItems.find((item) => String(item.name || '').toLowerCase() === 'open_template') ||
          approvedItems.find((item) => String(item.name || '').toLowerCase().includes('open')) ||
          approvedItems[0] ||
          null;
        return preferred ? String(preferred.id) : '';
      });
    } catch (error) {
      toast.error('Errore nel caricamento dei template Spoki');
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    loadConversations(1);
    loadCustomerLookup();
    loadTemplates();
  }, []);

  useEffect(() => {
    if (leftSidebarTab !== 'customers') {
      return undefined;
    }

    if (customerSearchTooShort) {
      setLoadingCustomerLookup(false);
      setCustomerLookup([]);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      loadCustomerLookup(customerSearch.trim());
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [customerSearch, customerSearchTooShort, leftSidebarTab]);

  useEffect(() => {
    if (loadingMessages) {
      return;
    }

    const currentConversationId = selectedConversation?.id || null;
    const previousConversationId = previousConversationIdRef.current;
    const previousMessagesCount = previousMessagesCountRef.current;
    const currentMessagesCount = messages.length;
    const conversationChanged = currentConversationId !== previousConversationId;
    const messageCountIncreased =
      Boolean(currentConversationId) &&
      currentConversationId === previousConversationId &&
      currentMessagesCount > previousMessagesCount;

    if (!currentConversationId) {
      setShowNewMessagesCta(false);
      previousConversationIdRef.current = null;
      previousMessagesCountRef.current = currentMessagesCount;
      return;
    }

    if (conversationChanged) {
      setShowNewMessagesCta(false);
      scrollMessagesToBottom('auto');
    } else if (messageCountIncreased) {
      if (isMessagesNearBottom()) {
        setShowNewMessagesCta(false);
        scrollMessagesToBottom('smooth');
      } else {
        setShowNewMessagesCta(true);
      }
    }

    previousConversationIdRef.current = currentConversationId;
    previousMessagesCountRef.current = currentMessagesCount;
  }, [loadingMessages, messages.length, selectedConversation]);

  useEffect(() => {
    const loadTemplateDetail = async () => {
      if (!selectedTemplateId) {
        setSelectedTemplateDetail(null);
        setSelectedTemplateLanguage('');
        setTemplateFields({});
        return;
      }
      try {
        setLoadingTemplateDetail(true);
        const res = await api.get(`/spoki/templates/${selectedTemplateId}`);
        const result = res.data || null;
        setSelectedTemplateDetail(result);
        const localizations = Array.isArray(result?.localizations) ? result.localizations : [];
        const nextLanguage =
          localizations.find((item) => String(item.language_code || '').toLowerCase().startsWith('it'))?.language_code ||
          localizations[0]?.language_code ||
          result?.template?.default_language ||
          'it';
        setSelectedTemplateLanguage(nextLanguage);
      } catch (error) {
        setSelectedTemplateDetail(null);
        setSelectedTemplateLanguage('');
        setTemplateFields({});
        toast.error('Errore nel caricamento del dettaglio template');
      } finally {
        setLoadingTemplateDetail(false);
      }
    };

    loadTemplateDetail();
  }, [selectedTemplateId]);

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    loadMessages(conv);
  };

  const handleRefresh = () => {
    loadConversations(meta.page || 1);
    loadCustomerLookup(customerSearch.trim());
  };

  const handleCustomerSearch = () => {
    loadCustomerLookup(customerSearch.trim());
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setLeftSidebarTab('conversations');
    const nextFilters = {
      ...filters,
      phone: customer.telephone || '',
      customer_id: customer.id ? String(customer.id) : ''
    };
    setFilters(nextFilters);
    try {
      setLoadingConversations(true);
      const params = { page: 1 };
      if (nextFilters.phone) params.phone = nextFilters.phone;
      if (nextFilters.customer_id) params.customer_id = nextFilters.customer_id;
      if (nextFilters.status) params.status = nextFilters.status;
      const res = await api.get('/spoki/chat/conversations', { params });
      const data = res.data || {};
      const items = data.data || [];
      const nextMeta = data.meta || { page: 1, totalPages: 1, total: 0 };
      setConversations(items);
      setMeta(nextMeta);
      loadConversationCustomers(items);
      const nextSelected = items[0] || null;
      setSelectedConversation(nextSelected);

      if (nextSelected) {
        const messageItems = await loadMessages(nextSelected);
        if (!hasActiveFreeTextWindow(messageItems)) {
          setTemplateModalOpen(true);
        }
      } else {
        setMessages([]);
        if (customer.telephone) {
          setTemplateModalOpen(true);
        }
      }
    } catch (error) {
      toast.error('Errore nel caricamento delle conversazioni Spoki');
    } finally {
      setLoadingConversations(false);
    }
  };

  const handleMarkAsRead = async () => {
    if (!selectedConversation) {
      return;
    }

    try {
      await api.post(`/spoki/chat/conversations/${selectedConversation.id}/mark-read`);
      setSelectedConversation((prev) =>
        prev ? { ...prev, unread_count: 0, has_unread: 0 } : prev
      );
      setConversations((prev) =>
        prev.map((conversation) =>
          conversation.id === selectedConversation.id
            ? { ...conversation, unread_count: 0, has_unread: 0 }
            : conversation
        )
      );
      toast.success('Conversazione segnata come letta');
    } catch (error) {
      toast.error('Impossibile segnare la conversazione come letta');
    }
  };

  const handleSendMessage = async () => {
    const targetPhone =
      selectedConversation?.phone || selectedCustomer?.telephone || filters.phone || '';
    const targetCustomerId =
      selectedConversation?.customer_id || selectedCustomer?.id || filters.customer_id || null;
    const hasRecentInboundWindow = hasActiveFreeTextWindow(messages);

    if (!targetPhone) {
      toast.error('Seleziona un cliente o una conversazione con un numero valido');
      return;
    }

    if (!selectedConversation || !hasRecentInboundWindow) {
      toast.error('Messaggio libero non disponibile: il cliente deve averti scritto nelle ultime 24 ore. Usa un template Spoki per riaprire la conversazione.');
      return;
    }

    if (!messageText.trim()) {
      toast.error('Inserisci un messaggio prima di inviare');
      return;
    }

    try {
      setSendingMessage(true);
      const payload = {
        phone: targetPhone,
        text: messageText.trim(),
        customer_id: targetCustomerId ? Number(targetCustomerId) : null,
        entity_type: targetCustomerId ? 'customer' : null,
        entity_id: targetCustomerId ? Number(targetCustomerId) : null,
        metadata: {
          source: 'spoki_chat_page'
        }
      };

      const res = await api.post('/spoki/messages/send-text', payload);
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Invio non riuscito');
      }

      toast.success('Messaggio inviato correttamente');
      setComposerExpanded(false);
      setMessageText('');

      const nextFilters = {
        ...filters,
        phone: targetPhone,
        customer_id: targetCustomerId ? String(targetCustomerId) : filters.customer_id
      };
      setFilters(nextFilters);
      await loadConversations(1, nextFilters);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore durante l’invio del messaggio');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleOpenAttachmentPicker = () => {
    attachmentInputRef.current?.click?.();
  };

  const handleAttachmentChange = (event) => {
    const nextFile = event.target?.files?.[0] || null;
    if (!nextFile) {
      return;
    }
    if (nextFile.size > ATTACHMENT_MAX_SIZE) {
      toast.error('Allegato troppo grande: massimo 25 MB');
      event.target.value = '';
      return;
    }
    if (!isAllowedAttachmentFile(nextFile)) {
      toast.error('Formato allegato non supportato');
      event.target.value = '';
      return;
    }
    setSelectedAttachment(nextFile);
  };

  const handleRemoveAttachment = () => {
    setSelectedAttachment(null);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const handleSendMedia = async () => {
    const targetPhone =
      selectedConversation?.phone || selectedCustomer?.telephone || filters.phone || '';
    const targetCustomerId =
      selectedConversation?.customer_id || selectedCustomer?.id || filters.customer_id || null;
    const hasRecentInboundWindow = hasActiveFreeTextWindow(messages);

    if (!targetPhone) {
      toast.error('Seleziona un cliente o una conversazione con un numero valido');
      return;
    }
    if (!selectedConversation || !hasRecentInboundWindow) {
      toast.error('Allegato non disponibile: il cliente deve averti scritto nelle ultime 24 ore. Usa un template Spoki per riaprire la conversazione.');
      return;
    }
    if (!selectedAttachment) {
      toast.error('Seleziona un allegato prima di inviare');
      return;
    }

    try {
      setSendingMessage(true);
      const formData = new FormData();
      formData.append('file', selectedAttachment);
      formData.append('phone', targetPhone);
      if (messageText.trim()) {
        formData.append('caption', messageText.trim());
      }
      if (targetCustomerId) {
        formData.append('customer_id', String(Number(targetCustomerId)));
        formData.append('entity_type', 'customer');
        formData.append('entity_id', String(Number(targetCustomerId)));
      }

      const res = await api.post('/spoki/messages/send-media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Invio allegato non riuscito');
      }

      toast.success('Allegato inviato correttamente');
      setComposerExpanded(false);
      setMessageText('');
      handleRemoveAttachment();

      const nextFilters = {
        ...filters,
        phone: targetPhone,
        customer_id: targetCustomerId ? String(targetCustomerId) : filters.customer_id
      };
      setFilters(nextFilters);
      await loadConversations(1, nextFilters);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore durante l’invio dell’allegato');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleTemplateFieldChange = (field, value) => {
    setTemplateFields((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOpenTemplateModal = () => {
    if (!chatTargetPhone) {
      toast.error('Seleziona un cliente o una conversazione con un numero valido');
      return;
    }
    setComposerExpanded(false);
    setTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = () => {
    if (sendingTemplate) {
      return;
    }
    setTemplateModalOpen(false);
  };

  const handleSendTemplate = async () => {
    const targetPhone =
      selectedConversation?.phone || selectedCustomer?.telephone || filters.phone || '';
    const targetCustomerId =
      selectedConversation?.customer_id || selectedCustomer?.id || filters.customer_id || null;

    if (!targetPhone) {
      toast.error('Seleziona un cliente o una conversazione con un numero valido');
      return;
    }

    if (!selectedTemplateId) {
      toast.error('Seleziona un template Spoki');
      return;
    }

    try {
      setSendingTemplate(true);
      const sanitizedFields = Object.fromEntries(
        Object.entries(templateFields).map(([key, value]) => [key, String(value ?? '').trim()])
      );
      const payload = {
        phone: targetPhone,
        template_id: Number(selectedTemplateId),
        language: selectedTemplateLanguage || 'it',
        fields: sanitizedFields,
        customer_id: targetCustomerId ? Number(targetCustomerId) : null,
        entity_type: targetCustomerId ? 'customer' : null,
        entity_id: targetCustomerId ? Number(targetCustomerId) : null,
        metadata: {
          source: 'spoki_chat_page_template'
        }
      };

      const res = await api.post('/spoki/messages/send-template', payload);
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Invio template non riuscito');
      }

      toast.success('Template inviato correttamente');
      setComposerExpanded(false);
      setTemplateModalOpen(false);
      const nextFilters = {
        ...filters,
        phone: targetPhone,
        customer_id: targetCustomerId ? String(targetCustomerId) : filters.customer_id
      };
      setFilters(nextFilters);
      await loadConversations(1, nextFilters);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore durante l’invio del template');
    } finally {
      setSendingTemplate(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('it-IT');
  };
  const formatMessageTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getMessageDateLabel = (value) => {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today.getTime() - target.getTime()) / 86400000);

    if (diffDays === 0) {
      return 'Oggi';
    }
    if (diffDays === 1) {
      return 'Ieri';
    }

    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPhone = (value) => {
    if (!value) {
      return 'Numero non disponibile';
    }
    return value;
  };
  const getInitials = (value) => {
    const words = String(value || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (!words.length) {
      return '?';
    }
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }
    return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
  };
  const formatConversationListStamp = (value) => {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const now = new Date();
    const sameDay =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (sameDay) {
      return date.toLocaleTimeString('it-IT', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {})
    });
  };
  const getCustomerFromConversation = (conversation) => {
    const customerId = conversation?.customer_id;
    if (!customerId) {
      return null;
    }
    return conversationCustomers[String(customerId)] || null;
  };
  const getConversationDisplayName = (conversation) => {
    const customer = getCustomerFromConversation(conversation);
    return customer?.full_name || formatPhone(conversation?.phone);
  };
  const selectedConversationCustomer = selectedConversation
    ? getCustomerFromConversation(selectedConversation)
    : null;
  const recipientDisplayName =
    selectedCustomer?.full_name ||
    selectedConversationCustomer?.full_name ||
    null;
  const recipientFirstName = recipientDisplayName
    ? recipientDisplayName.split(' ').filter(Boolean)[0] || ''
    : '';
  const activeContact = selectedConversationCustomer || selectedCustomer || null;
  const activeContactName =
    activeContact?.full_name ||
    (selectedConversation ? getConversationDisplayName(selectedConversation) : null) ||
    null;
  const activeContactPhone =
    selectedConversation?.phone || activeContact?.telephone || filters.phone || '';
  const activeContactEmail = activeContact?.email || '';
  const activeContactId = activeContact?.id || selectedConversation?.customer_id || null;
  const getStatusColor = (status) => {
    if (status === 'closed') {
      return 'default';
    }
    if (status === 'open') {
      return 'success';
    }
    return 'warning';
  };
  const getStatusLabel = (status) => {
    if (!status) {
      return 'Aperta';
    }
    if (status === 'open') {
      return 'Aperta';
    }
    if (status === 'closed') {
      return 'Chiusa';
    }
    return String(status);
  };
  const getMessageStatusLabel = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (!normalized) {
      return '';
    }
    if (normalized === 'error' || normalized === 'failed') {
      return 'Errore';
    }
    if (normalized === 'sent') {
      return 'Inviato';
    }
    if (normalized === 'delivered') {
      return 'Consegnato';
    }
    if (normalized === 'read') {
      return 'Letto';
    }
    if (normalized === 'pending') {
      return 'In attesa';
    }
    if (normalized === 'queued') {
      return 'In coda';
    }
    return String(status);
  };
  const getMessageTypeLabel = (type) => {
    const normalized = String(type || '').trim().toLowerCase();
    if (!normalized) {
      return '';
    }
    if (normalized === 'text') {
      return '';
    }
    if (normalized === 'image') {
      return 'immagine';
    }
    if (normalized === 'video') {
      return 'video';
    }
    if (normalized === 'audio') {
      return 'audio';
    }
    if (normalized === 'document') {
      return 'documento';
    }
    if (normalized === 'template') {
      return 'template';
    }
    return normalized;
  };
  const getMessageStatusMeta = (status) => {
    const label = getMessageStatusLabel(status);
    const normalized = String(status || '').trim().toLowerCase();

    if (normalized === 'error' || normalized === 'failed') {
      return {
        label,
        icon: CircleAlert,
        color: isDark ? '#fca5a5' : '#dc2626',
        bg: isDark ? 'rgba(239, 68, 68, 0.18)' : '#fee2e2'
      };
    }
    if (normalized === 'delivered' || normalized === 'read') {
      return {
        label,
        icon: CheckCheck,
        color: isDark ? '#86efac' : '#16a34a',
        bg: isDark ? 'rgba(34, 197, 94, 0.18)' : '#dcfce7'
      };
    }
    if (normalized === 'pending' || normalized === 'queued') {
      return {
        label,
        icon: Clock3,
        color: isDark ? '#fcd34d' : '#ca8a04',
        bg: isDark ? 'rgba(245, 158, 11, 0.18)' : '#fef3c7'
      };
    }
    return {
      label,
      icon: CheckCheck,
      color: isDark ? '#93c5fd' : '#2563eb',
      bg: isDark ? 'rgba(59, 130, 246, 0.18)' : '#dbeafe'
    };
  };

  const chatTargetPhone =
    selectedConversation?.phone || selectedCustomer?.telephone || filters.phone || '';
  const hasRecentInboundWindow = useMemo(
    () => Boolean(selectedConversation && hasActiveFreeTextWindow(messages)),
    [messages, selectedConversation]
  );
  const templateModeActive = Boolean(chatTargetPhone && (!selectedConversation || !hasRecentInboundWindow));
  const approvedTemplateOptions = useMemo(
    () => templateOptions.filter((item) => deriveTemplateStatus(item) === 'approved'),
    [templateOptions]
  );
  const hasApprovedTemplates = approvedTemplateOptions.length > 0;
  const templateLocalizations = Array.isArray(selectedTemplateDetail?.localizations)
    ? selectedTemplateDetail.localizations
    : [];
  const selectedTemplateLocalization =
    templateLocalizations.find(
      (item) => String(item.language_code || '').toLowerCase() === String(selectedTemplateLanguage || '').toLowerCase()
    ) ||
    templateLocalizations[0] ||
    null;
  const selectedTemplateStatus = String(
    selectedTemplateLocalization?.status || selectedTemplateDetail?.template?.status || ''
  ).trim();
  const templatePreviewBody = renderTemplatePreview(
    selectedTemplateLocalization?.body_text || '',
    templateFields
  );

  useEffect(() => {
    if (!selectedTemplateLocalization) {
      setTemplateFields({});
      return;
    }
    setTemplateFields(
      buildTemplateFieldDefaults(selectedTemplateLocalization, {
        fallbackMessage: messageText.trim(),
        recipientFirstName
      })
    );
  }, [selectedTemplateLocalization, messageText, recipientFirstName]);

  useEffect(() => {
    setComposerExpanded(false);
    setSelectedAttachment(null);
  }, [selectedConversation?.id, selectedCustomer?.id]);

  const composerHint = !chatTargetPhone
    ? 'Seleziona prima un nominativo dalla rubrica o una conversazione esistente.'
    : null;
  const composerTitle = templateModeActive ? 'Riapri con template' : 'Nuovo messaggio';
  const composerSubtitle = templateModeActive
    ? hasApprovedTemplates
      ? 'Apri il pannello per scegliere come riaprire la conversazione.'
      : (
        <>
          Non ci sono template <Box component="span" sx={{ fontWeight: 700 }}>approvati</Box> disponibili per questo canale.
        </>
      )
    : 'Apri il composer per scrivere un nuovo messaggio WhatsApp.';
  const canSendTextMessage = Boolean(
    chatTargetPhone &&
      selectedConversation &&
      hasRecentInboundWindow &&
      !selectedAttachment &&
      messageText.trim() &&
      !sendingMessage
  );
  const canSendAttachmentMessage = Boolean(
    chatTargetPhone &&
      selectedConversation &&
      hasRecentInboundWindow &&
      selectedAttachment &&
      !sendingMessage
  );
  const canSendTemplate = Boolean(
    chatTargetPhone &&
      selectedTemplateId &&
      selectedTemplateLanguage &&
      !sendingTemplate &&
      !loadingTemplateDetail
  );

  useEffect(() => {
    if (!approvedTemplateOptions.length) {
      if (selectedTemplateId) {
        setSelectedTemplateId('');
      }
      return;
    }

    const selectedIsApproved = approvedTemplateOptions.some(
      (item) => String(item.id) === String(selectedTemplateId)
    );

    if (!selectedTemplateId || !selectedIsApproved) {
      setSelectedTemplateId(String(approvedTemplateOptions[0].id));
    }
  }, [approvedTemplateOptions, selectedTemplateId]);
  const parseConversationMetadata = (conversation) => {
    if (!conversation?.metadata) {
      return {};
    }

    if (typeof conversation.metadata === 'object') {
      return conversation.metadata;
    }

    try {
      return JSON.parse(conversation.metadata);
    } catch (_) {
      return {};
    }
  };
  const isGroupConversation = (conversation) => {
    const metadata = parseConversationMetadata(conversation);
    return Boolean(
      metadata.group ||
      metadata.is_group ||
      metadata.chat_type === 'group' ||
      metadata.conversation_type === 'group'
    );
  };
  const visibleConversations = conversations.filter((conversation) => {
    if (conversationTab === 'unread') {
      return Boolean(conversation.has_unread);
    }
    return true;
  }).filter((conversation) => {
    const term = conversationSearch.trim().toLowerCase();
    if (!term) {
      return true;
    }

    const haystack = [
      getConversationDisplayName(conversation),
      conversation?.phone,
      conversation?.last_message_text,
      conversation?.customer_id ? `cliente ${conversation.customer_id}` : ''
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(term);
  });
  const getConversationDate = (conversation) => conversation?.last_message_at || conversation?.created_at || null;
  const getConversationSectionLabel = (conversation) => {
    const rawValue = getConversationDate(conversation);
    if (!rawValue) {
      return 'Senza data';
    }

    const date = new Date(rawValue);
    if (Number.isNaN(date.getTime())) {
      return 'Senza data';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today.getTime() - target.getTime()) / 86400000);

    if (diffDays === 0) {
      return 'Oggi';
    }
    if (diffDays === 1) {
      return 'Ieri';
    }

    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short'
    });
  };
  const groupedConversations = visibleConversations.reduce((acc, conversation) => {
    const label = getConversationSectionLabel(conversation);
    if (!acc[label]) {
      acc[label] = [];
    }
    acc[label].push(conversation);
    return acc;
  }, {});
  const groupedConversationEntries = Object.entries(groupedConversations);
  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              bgcolor: ui.iconBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MessageCircle size={16} color={ui.iconColor} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary', lineHeight: 1.1, mb: 0 }}>
              Chat Spoki
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
              Messaggi inviati e webhook ricevuti
            </Typography>
          </Box>
        </Box>
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={
            selectedConversation
              ? `Chat #${selectedConversation.id}`
              : selectedCustomer
                ? selectedCustomer.full_name || `Cliente ${selectedCustomer.id}`
                : 'Nessuna selezione'
          }
        />
      </Box>
      <StyledCard>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={1.5}>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                alignSelf: 'flex-start',
                position: { xs: 'static', md: 'sticky' },
                top: { md: 16 }
              }}
            >
              <Box
                sx={{
                  height: { xs: '72vh', md: 620 },
                  minHeight: { xs: 420, md: 620 },
                  maxHeight: { xs: 620, md: 620 },
                  borderRadius: ui.radiusPanel,
                  border: ui.panelBorder,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  bgcolor: ui.surface
                }}
              >
                <Box
                  sx={{
                    p: 1.1,
                    borderBottom: ui.panelBorderSoft,
                    bgcolor: ui.surfaceAlt
                  }}
                >
                  <Tabs
                    value={leftSidebarTab}
                    onChange={(_, value) => setLeftSidebarTab(value)}
                    variant="fullWidth"
                    textColor="inherit"
                    indicatorColor="primary"
                    sx={{
                      minHeight: 0,
                      gap: 0.75,
                      '& .MuiTabs-indicator': {
                        display: 'none'
                      },
                      '& .MuiTab-root': {
                        minHeight: 40,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: 13,
                        px: 0.75,
                        borderRadius: ui.radiusSoft,
                        color: 'text.secondary',
                        bgcolor: ui.tabBg
                      },
                      '& .MuiTab-root.Mui-selected': {
                        color: 'text.primary',
                        bgcolor: ui.tabSelectedBlue,
                        boxShadow: 'none'
                      }
                    }}
                  >
                    <Tab label="Conversazioni" value="conversations" />
                    <Tab label="Rubrica clienti" value="customers" />
                  </Tabs>
                </Box>

                {leftSidebarTab === 'customers' ? (
                  <>
                    <Box sx={{ p: 0.85, borderBottom: ui.panelBorderSoft }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        placeholder="Cerca nominativo, telefono o email..."
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleCustomerSearch();
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search size={16} />
                            </InputAdornment>
                          ),
                          endAdornment: customerSearch ? (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                size="small"
                                aria-label="Pulisci ricerca"
                                onClick={() => setCustomerSearch('')}
                              >
                                <X size={14} />
                              </IconButton>
                            </InputAdornment>
                          ) : null
                        }}
                        helperText={
                          customerSearchTooShort
                            ? `Inserisci almeno ${CUSTOMER_SEARCH_MIN_LENGTH} caratteri`
                            : undefined
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            minHeight: 38,
                            borderRadius: ui.radiusSoft
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 1
                          },
                          '& .MuiFormHelperText-root': {
                            mt: 0.5,
                            mb: 0
                          }
                        }}
                        FormHelperTextProps={{
                          sx: { minHeight: customerSearchTooShort ? 20 : 0, mt: 0.5 }
                        }}
                      />
                    </Box>
                    <List
                      dense
                      disablePadding
                      sx={{
                        overflow: 'auto',
                        minHeight: 0,
                        flex: 1,
                        px: 0.5,
                        py: 0.5
                      }}
                    >
                      {loadingCustomerLookup ? (
                        <ListItem sx={{ justifyContent: 'center', py: 2 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                              Caricamento nominativi...
                            </Typography>
                          </Stack>
                        </ListItem>
                      ) : null}
                      {!loadingCustomerLookup && customerSearchTooShort ? (
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="text.secondary">
                                Digita almeno {CUSTOMER_SEARCH_MIN_LENGTH} caratteri per cercare nella rubrica.
                              </Typography>
                            }
                          />
                        </ListItem>
                      ) : null}
                      {!loadingCustomerLookup && !customerSearchTooShort && customerLookup.map((customer) => (
                        <React.Fragment key={customer.id}>
                          <ListItem disablePadding sx={{ mb: 0.35 }}>
                            <ListItemButton
                              selected={selectedCustomer?.id === customer.id}
                              onClick={() => handleSelectCustomer(customer)}
                              sx={{
                                alignItems: 'flex-start',
                                py: 0.75,
                                px: 0.85,
                                borderRadius: ui.radiusCard,
                                border: '1px solid transparent',
                                bgcolor: 'transparent',
                                transition: 'background-color 120ms ease, border-color 120ms ease',
                                '&:hover': {
                                  bgcolor: ui.hoverBg,
                                  borderColor: isDark
                                    ? 'rgba(148, 163, 184, 0.12)'
                                    : 'rgba(100, 116, 139, 0.12)'
                                },
                                '&.Mui-selected': {
                                  bgcolor: ui.selectedBg,
                                  borderColor: ui.selectedBorder
                                },
                                '&.Mui-selected:hover': {
                                  bgcolor: ui.selectedHoverBg,
                                  borderColor: ui.selectedBorder
                                }
                              }}
                            >
                              <ListItemAvatar sx={{ minWidth: 40 }}>
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: selectedCustomer?.id === customer.id ? ui.iconBg : ui.surfaceMuted,
                                    color: selectedCustomer?.id === customer.id ? ui.iconColor : ui.metaIcon
                                  }}
                                >
                                  <UserRound size={14} />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, lineHeight: 1.15, fontSize: 13.5 }}
                                    noWrap
                                  >
                                    {renderHighlightedText(
                                      customer.full_name || `Cliente ${customer.id}`,
                                      trimmedCustomerSearch
                                    )}
                                  </Typography>
                                }
                                secondary={
                                  <Box sx={{ mt: 0.2 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block', lineHeight: 1.15, fontSize: 11.5 }}
                                    >
                                      {renderHighlightedText(
                                        customer.telephone || 'Telefono non disponibile',
                                        trimmedCustomerSearch
                                      )}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block', lineHeight: 1.15, fontSize: 11.5 }}
                                      noWrap
                                    >
                                      {renderHighlightedText(
                                        customer.email || '',
                                        trimmedCustomerSearch
                                      )}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        </React.Fragment>
                      ))}
                      {!loadingCustomerLookup && !customerSearchTooShort && customerLookup.length === 0 && (
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="text.secondary">
                                Nessun cliente trovato
                              </Typography>
                            }
                          />
                        </ListItem>
                      )}
                    </List>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        px: 1.1,
                        py: 0.9,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: ui.panelBorderSoft
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Elenco conversazioni
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleRefresh}
                        disabled={loadingConversations}
                        sx={{ color: 'text.secondary' }}
                      >
                        <RefreshCw size={16} />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        p: 1.1,
                        borderBottom: ui.panelBorderSoft,
                        bgcolor: ui.surfaceAlt
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        value={conversationSearch}
                        onChange={(e) => setConversationSearch(e.target.value)}
                        placeholder="Cerca conversazione..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search size={16} />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.9,
                        borderBottom: ui.panelBorderSoft,
                        bgcolor: ui.surface
                      }}
                    >
                      <Tabs
                        value={conversationTab}
                        onChange={(_, value) => setConversationTab(value)}
                        variant="fullWidth"
                        textColor="inherit"
                        indicatorColor="primary"
                        sx={{
                          minHeight: 0,
                          gap: 0.75,
                          '& .MuiTabs-indicator': {
                            display: 'none'
                          },
                          '& .MuiTab-root': {
                            minHeight: 40,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: 12,
                            px: 0.5,
                            borderRadius: ui.radiusSoft,
                            color: 'text.secondary',
                            bgcolor: ui.tabBg
                          },
                          '& .MuiTab-root.Mui-selected': {
                            color: 'text.primary',
                            bgcolor: ui.tabSelectedGreen,
                            boxShadow: 'none'
                          }
                        }}
                      >
                        <Tab label="Tutte" value="all" />
                        <Tab label="Non lette" value="unread" />
                      </Tabs>
                    </Box>
                    <List
                      dense
                      disablePadding
                      sx={{
                        overflow: 'auto',
                        flex: 1,
                        minHeight: 0,
                        px: 0.75,
                        py: 0.75
                      }}
                    >
                      {groupedConversationEntries.map(([label, items]) => (
                        <React.Fragment key={label}>
                          <ListItem sx={{ px: 0.75, py: 0.25 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 800,
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: 0.4
                              }}
                            >
                              {label}
                            </Typography>
                          </ListItem>
                          {items.map((c) => (
                            <ListItem key={c.id} disablePadding sx={{ mb: 0 }}>
                              <ListItemButton
                                selected={selectedConversation && selectedConversation.id === c.id}
                                onClick={() => selectConversation(c)}
                                sx={{
                                  position: 'relative',
                                  alignItems: 'flex-start',
                                  py: 1,
                                  px: 1.1,
                                  borderRadius: 0,
                                  borderBottom: ui.panelBorderSoft,
                                  bgcolor: 'transparent',
                                  boxShadow: 'none',
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: 8,
                                    bottom: 8,
                                    width: 3,
                                    borderRadius: 999,
                                    bgcolor: 'transparent',
                                    transition: 'background-color 120ms ease'
                                  },
                                  '&.Mui-selected': {
                                    bgcolor: ui.selectedBg
                                  },
                                  '&.Mui-selected:hover': {
                                    bgcolor: ui.selectedHoverBg
                                  },
                                  '&.Mui-selected::before': {
                                    bgcolor: ui.selectedBorder
                                  },
                                  '&:hover': {
                                    bgcolor: ui.hoverBg
                                  }
                                }}
                              >
                                <ListItemAvatar sx={{ minWidth: 54, mt: 0.2 }}>
                                  <Badge
                                    color="primary"
                                    overlap="circular"
                                    badgeContent={c.has_unread ? c.unread_count || 1 : 0}
                                    invisible={!c.has_unread}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 44,
                                        height: 44,
                                        bgcolor: ui.avatarBg,
                                        color: 'common.white',
                                        fontWeight: 800,
                                        fontSize: 18
                                      }}
                                    >
                                      {getInitials(getConversationDisplayName(c))}
                                    </Avatar>
                                  </Badge>
                                </ListItemAvatar>
                                <ListItemText
                                  sx={{ my: 0 }}
                                  primary={
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25 }}>
                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: 800, lineHeight: 1.2, mb: 0.35 }}
                                          noWrap
                                        >
                                          {getConversationDisplayName(c)}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{
                                            lineHeight: 1.25,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                          }}
                                        >
                                          {c.last_message_text || formatPhone(c.phone)}
                                        </Typography>
                                        <Stack
                                          direction="row"
                                          spacing={0.75}
                                          sx={{ mt: 0.65, minWidth: 0 }}
                                          alignItems="center"
                                        >
                                          <Typography variant="caption" color="text.secondary" noWrap>
                                            {c.customer_id ? `Cliente ${c.customer_id}` : formatPhone(c.phone)}
                                          </Typography>
                                          {isGroupConversation(c) ? (
                                            <Chip size="small" color="warning" variant="outlined" label="Gruppo" />
                                          ) : null}
                                        </Stack>
                                      </Box>
                                      <Stack spacing={0.9} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                                        <Chip
                                          size="small"
                                          label={getStatusLabel(c.status)}
                                          sx={{
                                            height: 24,
                                            borderRadius: ui.radiusSoft,
                                            border: ui.panelBorderSoft,
                                            bgcolor:
                                              c.status === 'open'
                                                ? ui.statusOpenBg
                                                : c.status === 'closed'
                                                  ? ui.statusClosedBg
                                                  : ui.statusOtherBg,
                                            color: ui.statusText,
                                            fontWeight: 700
                                          }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                          {formatConversationListStamp(c.last_message_at)}
                                        </Typography>
                                      </Stack>
                                    </Box>
                                  }
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </React.Fragment>
                      ))}
                      {visibleConversations.length === 0 && (
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="text.secondary">
                                {loadingConversations
                                  ? 'Caricamento conversazioni...'
                                  : conversationTab === 'unread'
                                    ? 'Nessuna conversazione non letta'
                                    : 'Nessuna conversazione trovata'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      )}
                    </List>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 1.1,
                        py: 0.9,
                        borderTop: ui.panelBorderSoft
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Pagina {meta.page} di {meta.totalPages || 1}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          disabled={meta.page <= 1}
                          onClick={() => loadConversations(meta.page - 1)}
                        >
                          Prec.
                        </Button>
                        <Button
                          size="small"
                          disabled={meta.page >= meta.totalPages}
                          onClick={() => loadConversations(meta.page + 1)}
                        >
                          Succ.
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: ui.radiusPanel,
                  border: ui.panelBorder,
                  display: 'flex',
                  flexDirection: 'column',
                  height: { xs: 560, md: 620 },
                  position: 'relative',
                  overflow: 'hidden',
                  bgcolor: ui.surface
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: ui.panelBorderSoft,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: ui.surface
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {selectedConversation
                        ? `Chat con ${getConversationDisplayName(selectedConversation)}`
                        : selectedCustomer
                          ? `Chat cliente ${selectedCustomer.full_name || `#${selectedCustomer.id}`}`
                          : 'Seleziona una conversazione'}
                    </Typography>
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                      {!selectedConversation && selectedCustomer ? (
                        <Chip size="small" variant="outlined" label={`Cliente ${selectedCustomer.id}`} />
                      ) : null}
                      {!selectedConversation && selectedCustomer?.telephone ? (
                        <Chip size="small" variant="outlined" label={selectedCustomer.telephone} />
                      ) : null}
                      {selectedConversation?.customer_id ? (
                        <Chip size="small" variant="outlined" label={`Cliente ${selectedConversation.customer_id}`} />
                      ) : null}
                      {selectedConversation ? (
                        <Chip
                          size="small"
                          color={getStatusColor(selectedConversation.status)}
                          label={getStatusLabel(selectedConversation.status)}
                        />
                      ) : null}
                      {selectedConversation?.has_unread ? (
                        <Chip
                          size="small"
                          color="primary"
                          label={`${selectedConversation.unread_count || 0} non letti`}
                        />
                      ) : null}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => selectedConversation && loadMessages(selectedConversation)}
                      disabled={!selectedConversation || loadingMessages}
                    >
                      Aggiorna
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CheckCheck size={16} />}
                      onClick={handleMarkAsRead}
                      disabled={!selectedConversation || !selectedConversation.has_unread}
                    >
                      Segna letta
                    </Button>
                  </Stack>
                </Box>
                <Box
                  ref={messagesContainerRef}
                  onScroll={handleMessagesScroll}
                  sx={{
                    flex: 1,
                    p: 1.5,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    bgcolor: ui.messageAreaBg,
                    backgroundImage: ui.messageAreaPattern,
                    backgroundSize: ui.messageAreaPatternSize,
                    backgroundPosition: '0 0, 24px 24px, 0 0'
                  }}
                >
                  {selectedConversation ? (
                    loadingMessages ? (
                      <Typography variant="body2" color="text.secondary">
                        Caricamento messaggi...
                      </Typography>
                    ) : messages.length > 0 ? (
                      messages.map((m, index) => {
                        const statusMeta =
                          m.direction === 'outbound' && m.status
                            ? getMessageStatusMeta(m.status)
                            : null;
                        const mediaItems = extractMediaItemsFromPayload(m.payload);
                        const primaryMedia = mediaItems[0] || null;
                        const imageMedia = primaryMedia && isImageMediaItem(primaryMedia) ? primaryMedia : null;
                        const documentMedia = primaryMedia && isDocumentMediaItem(primaryMedia) ? primaryMedia : null;
                        const StatusIcon = statusMeta?.icon || null;
                        const currentMessageDateLabel = getMessageDateLabel(m.sent_at);
                        const previousMessageDateLabel =
                          index > 0 ? getMessageDateLabel(messages[index - 1]?.sent_at) : '';
                        const showDateSeparator =
                          Boolean(currentMessageDateLabel) &&
                          currentMessageDateLabel !== previousMessageDateLabel;

                        return (
                          <React.Fragment key={m.id}>
                            {showDateSeparator ? (
                              <Box
                                sx={{
                                  alignSelf: 'center',
                                  px: 1.5,
                                  py: 0.45,
                                  mb: 0.75,
                                  borderRadius: 1.25,
                                  bgcolor: isDark ? 'rgba(32, 44, 51, 0.88)' : 'rgba(255, 255, 255, 0.72)',
                                  color: isDark ? '#e9edef' : '#54656f',
                                  boxShadow: ui.bubbleShadow,
                                  backdropFilter: 'blur(2px)',
                                  textTransform: 'capitalize',
                                  fontSize: 13,
                                  lineHeight: 1.2
                                }}
                              >
                                {currentMessageDateLabel}
                              </Box>
                            ) : null}
                            <Box
                              sx={{
                                alignSelf: m.direction === 'outbound' ? 'flex-end' : 'flex-start',
                                maxWidth: '76%'
                              }}
                            >
                              <Box
                                sx={{
                                  bgcolor:
                                    m.direction === 'outbound'
                                      ? ui.outboundBubbleBg
                                      : ui.inboundBubbleBg,
                                  color:
                                    m.direction === 'outbound'
                                      ? ui.outboundBubbleText
                                      : ui.inboundBubbleText,
                                  borderRadius:
                                    m.direction === 'outbound'
                                      ? '18px 18px 4px 18px'
                                      : '18px 18px 18px 4px',
                                  px: 1.2,
                                  py: 0.95,
                                  boxShadow: ui.bubbleShadow,
                                  border:
                                    m.direction === 'outbound'
                                      ? 'none'
                                      : ui.panelBorderSoft
                                }}
                              >
                                {imageMedia ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    <Box
                                      component="a"
                                      href={imageMedia.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      sx={{
                                        display: 'block',
                                        lineHeight: 0,
                                        borderRadius: 1.5,
                                        overflow: 'hidden'
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={imageMedia.url}
                                        alt={imageMedia.title || 'Immagine ricevuta'}
                                        sx={{
                                          display: 'block',
                                          width: '100%',
                                          maxWidth: 320,
                                          maxHeight: 260,
                                          objectFit: 'cover',
                                          borderRadius: 1.5,
                                          bgcolor: ui.surfaceMuted
                                        }}
                                      />
                                    </Box>
                                    {m.preview_text ? (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          whiteSpace: 'pre-wrap',
                                          lineHeight: 1.45,
                                          fontSize: 15
                                        }}
                                      >
                                        {m.preview_text}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                ) : documentMedia ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 165 }}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 0.75,
                                        borderRadius: 999,
                                        border: ui.panelBorderSoft,
                                        bgcolor: m.direction === 'outbound' ? 'rgba(255,255,255,0.18)' : ui.surfaceAlt,
                                        px: 0.65,
                                        py: 0.5
                                      }}
                                    >
                                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                                      <Box
                                        sx={{
                                          width: 28,
                                          height: 28,
                                          borderRadius: 1,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          bgcolor: isPdfMediaItem(documentMedia)
                                            ? (isDark ? 'rgba(248, 113, 113, 0.18)' : '#fee2e2')
                                            : ui.accentSoftBg,
                                          color: isPdfMediaItem(documentMedia)
                                            ? (isDark ? '#fca5a5' : '#dc2626')
                                            : ui.iconColor,
                                          flexShrink: 0
                                        }}
                                      >
                                        <FileText size={13} />
                                      </Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          lineHeight: 1,
                                          fontSize: 12.5,
                                          fontWeight: 700
                                        }}
                                        noWrap
                                      >
                                        {getMediaDisplayTitle(documentMedia)}
                                      </Typography>
                                      </Stack>
                                      <Button
                                        component="a"
                                        href={documentMedia.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        size="small"
                                        variant="text"
                                        sx={{
                                          minWidth: 0,
                                          px: 0.35,
                                          py: 0.2,
                                          borderRadius: 0.9,
                                          fontSize: 12,
                                          fontWeight: 700
                                        }}
                                      >
                                        Apri
                                      </Button>
                                    </Box>
                                    {m.preview_text ? (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          whiteSpace: 'pre-wrap',
                                          lineHeight: 1.45,
                                          fontSize: 15
                                        }}
                                      >
                                        {m.preview_text}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      whiteSpace: 'pre-wrap',
                                      lineHeight: 1.45,
                                      fontSize: 15
                                    }}
                                  >
                                    {m.preview_text || '[contenuto non disponibile]'}
                                  </Typography>
                                )}
                                <Stack
                                  direction="row"
                                  spacing={0.4}
                                  justifyContent="flex-end"
                                  alignItems="center"
                                  sx={{
                                    mt: 0.5,
                                    color: ui.bubbleMetaText,
                                    opacity: 0.9
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'inherit', lineHeight: 1, fontSize: 11, letterSpacing: 0 }}
                                  >
                                    {formatMessageTime(m.sent_at)}
                                  </Typography>
                                  {getMessageTypeLabel(m.type) ? (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'inherit', lineHeight: 1, fontSize: 11, letterSpacing: 0 }}
                                    >
                                      • {getMessageTypeLabel(m.type)}
                                    </Typography>
                                  ) : null}
                                  {statusMeta && StatusIcon ? (
                                    <Box
                                      title={statusMeta.label}
                                      sx={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: statusMeta.color,
                                        opacity: 0.9
                                      }}
                                    >
                                      <StatusIcon size={10} strokeWidth={2} />
                                    </Box>
                                  ) : null}
                                </Stack>
                              </Box>
                            </Box>
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Nessun messaggio disponibile per questa conversazione
                        </Typography>
                      </Box>
                    )
                  ) : (
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {selectedCustomer
                          ? `Nessuna conversazione Spoki trovata per ${selectedCustomer.full_name || `il cliente ${selectedCustomer.id}`}`
                          : 'Seleziona una conversazione dalla lista per vedere i messaggi'}
                      </Typography>
                    </Box>
                  )}
                  {showNewMessagesCta ? (
                    <Box
                      sx={{
                        position: 'sticky',
                        bottom: 8,
                        display: 'flex',
                        justifyContent: 'center',
                        zIndex: 1,
                        pointerEvents: 'none'
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setShowNewMessagesCta(false);
                          scrollMessagesToBottom('smooth');
                        }}
                        sx={{
                          borderRadius: ui.radiusPill,
                          boxShadow: 'none',
                          pointerEvents: 'auto'
                        }}
                      >
                        Nuovi messaggi
                      </Button>
                    </Box>
                  ) : null}
                  <Box ref={messagesEndRef} sx={{ height: 1 }} />
                </Box>
                <Box
                  sx={{
                    p: 1.1,
                    borderTop: ui.panelBorderSoft,
                    bgcolor: ui.surface,
                    position: 'relative'
                  }}
                >
                  {composerExpanded ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 12,
                        right: 12,
                        bottom: 68,
                        zIndex: 4,
                        borderRadius: ui.radiusPanel,
                        border: ui.panelBorderStrong,
                        bgcolor: ui.composerPanelBg,
                        boxShadow: '0 18px 40px rgba(15, 23, 42, 0.12)',
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1.1,
                          borderBottom: ui.panelBorderSoft,
                          bgcolor: ui.composerHeaderBg
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box
                              sx={{
                                px: 1.1,
                                py: 0.6,
                                borderRadius: ui.radiusSoft,
                                bgcolor: ui.surface,
                                border: ui.panelBorderSoft,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75
                              }}
                            >
                              <MessageCircle size={16} color={ui.iconColor} />
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                WhatsApp
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {templateModeActive ? 'Template di riapertura' : 'Messaggio'}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" onClick={() => setComposerExpanded(false)}>
                              <ChevronDown size={16} />
                            </IconButton>
                            <IconButton size="small" onClick={() => setComposerExpanded(false)}>
                              <X size={16} />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Box>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.9,
                          borderBottom: ui.panelBorderSoft,
                          display: 'flex',
                          gap: 2,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 0.75 }}>
                            Da:
                          </Box>
                          Canale Spoki
                        </Typography>
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 0.75 }}>
                            A:
                          </Box>
                          {chatTargetPhone || 'Numero non selezionato'}
                        </Typography>
                      </Box>
                      <input
                        ref={attachmentInputRef}
                        type="file"
                        hidden
                        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                        onChange={handleAttachmentChange}
                      />
                      <Box sx={{ p: 1.5 }}>
                        {templateModeActive ? (
                          <Stack spacing={1.25}>
                            <Typography variant="body2" color="text.secondary">
                              Il testo libero non e' disponibile: per iniziare o riaprire la conversazione serve un template Spoki.
                            </Typography>
                            <Box
                              sx={{
                                borderRadius: ui.radiusCard,
                                border: ui.panelBorder,
                                bgcolor: ui.surfaceAlt,
                                p: 1.25
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.35 }}>
                                {hasApprovedTemplates
                                  ? selectedTemplateDetail?.template?.name || 'Template approvato'
                                  : 'Nessun template approvato'}
                              </Typography>
                              <Typography
                                variant="caption"
                                color={hasApprovedTemplates ? 'text.secondary' : 'error.main'}
                              >
                                {hasApprovedTemplates
                                  ? `${selectedTemplateLocalization
                                      ? languageLabel(selectedTemplateLocalization.language_code)
                                      : 'Template da selezionare'}${selectedTemplateStatus ? ` • ${selectedTemplateStatus}` : ''}`
                                  : 'Sincronizza o approva un template Spoki per poter riaprire la conversazione.'}
                              </Typography>
                              {templatePreviewBody ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                                >
                                  {templatePreviewBody}
                                </Typography>
                              ) : null}
                            </Box>
                          </Stack>
                        ) : (
                          <Stack spacing={1.1}>
                            {selectedAttachment ? (
                              <Box
                                sx={{
                                  borderRadius: ui.radiusCard,
                                  border: ui.panelBorder,
                                  bgcolor: ui.surfaceAlt,
                                  px: 1.1,
                                  py: 0.9
                                }}
                              >
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                    <Box
                                      sx={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: ui.radiusSoft,
                                        bgcolor: ui.accentSoftBg,
                                        color: ui.iconColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                      }}
                                    >
                                      <Paperclip size={16} />
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                        {selectedAttachment.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {`${getAttachmentSummaryLabel(selectedAttachment)} • ${formatFileSize(selectedAttachment.size)}`}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                  <IconButton size="small" onClick={handleRemoveAttachment}>
                                    <X size={14} />
                                  </IconButton>
                                </Stack>
                              </Box>
                            ) : null}
                            <TextField
                              fullWidth
                              multiline
                              minRows={selectedAttachment ? 3 : 6}
                              maxRows={10}
                              placeholder={
                                selectedAttachment
                                  ? 'Aggiungi una didascalia opzionale...'
                                  : 'Digita un messaggio...'
                              }
                              value={messageText}
                              onChange={(e) => setMessageText(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey && !selectedAttachment) {
                                  e.preventDefault();
                                  if (canSendTextMessage) {
                                    handleSendMessage();
                                  }
                                }
                              }}
                              disabled={Boolean(!chatTargetPhone || sendingMessage || !selectedConversation || !hasRecentInboundWindow)}
                            />
                          </Stack>
                        )}
                      </Box>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderTop: ui.panelBorderSoft,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Stack direction="row" spacing={0.25} alignItems="center">
                          {!templateModeActive ? (
                            <IconButton
                              size="small"
                              onClick={handleOpenAttachmentPicker}
                              disabled={!chatTargetPhone || sendingMessage || !selectedConversation || !hasRecentInboundWindow}
                            >
                              <Paperclip size={16} />
                            </IconButton>
                          ) : null}
                          <Typography variant="caption" color="text.secondary">
                            {templateModeActive
                              ? activeContactName || chatTargetPhone || 'Riapertura conversazione'
                              : selectedAttachment
                              ? `${getAttachmentSummaryLabel(selectedAttachment)} pronto per l'invio`
                              : `${messageText.trim().length} caratteri`}
                          </Typography>
                        </Stack>
                        {templateModeActive ? (
                          <Button
                            variant="contained"
                            startIcon={<SendHorizontal size={16} />}
                            onClick={handleOpenTemplateModal}
                            disabled={!chatTargetPhone || !hasApprovedTemplates}
                          >
                            Apri template
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            startIcon={<SendHorizontal size={16} />}
                            onClick={selectedAttachment ? handleSendMedia : handleSendMessage}
                            disabled={selectedAttachment ? !canSendAttachmentMessage : !canSendTextMessage}
                          >
                            {sendingMessage ? 'Invio...' : selectedAttachment ? 'Invia allegato' : 'Invia'}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ) : null}
                  <Box
                    onClick={() => {
                      if (chatTargetPhone) {
                        setComposerExpanded((prev) => !prev);
                      }
                    }}
                    sx={{
                      borderRadius: ui.radiusCard,
                      border: ui.panelBorder,
                      bgcolor: chatTargetPhone ? ui.composerDockBg : ui.surfaceMuted,
                      px: 1.25,
                      py: 1,
                      cursor: chatTargetPhone ? 'pointer' : 'not-allowed',
                      opacity: chatTargetPhone ? 1 : 0.75
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: ui.radiusSoft,
                            bgcolor: ui.accentSoftBg,
                            color: ui.iconColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <PencilLine size={18} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {composerTitle}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {composerHint || composerSubtitle}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {chatTargetPhone ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={chatTargetPhone}
                            sx={{ maxWidth: 150 }}
                          />
                        ) : null}
                        {composerExpanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                alignSelf: 'flex-start',
                position: { xs: 'static', md: 'sticky' },
                top: { md: 16 }
              }}
            >
              <Box
                sx={{
                  height: { xs: 'auto', md: 620 },
                  minHeight: 0,
                  borderRadius: ui.radiusPanel,
                  border: ui.panelBorder,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  bgcolor: ui.surface
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.2,
                    borderBottom: ui.panelBorderSoft,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Dettagli contatto
                  </Typography>
                  <Chip size="small" variant="outlined" label="Live" />
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                  {activeContactName || activeContactPhone ? (
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar
                          sx={{
                            width: 52,
                            height: 52,
                            bgcolor: ui.avatarBg,
                            color: 'common.white',
                            fontWeight: 800,
                            fontSize: 20
                          }}
                        >
                          {getInitials(activeContactName || activeContactPhone)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
                            {activeContactName || activeContactPhone}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {activeContactPhone || 'Numero non disponibile'}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {activeContactId ? (
                          <Chip size="small" color="primary" variant="outlined" label={`Cliente ${activeContactId}`} />
                        ) : null}
                        {selectedConversation?.status ? (
                          <Chip
                            size="small"
                            color={getStatusColor(selectedConversation.status)}
                            label={getStatusLabel(selectedConversation.status)}
                          />
                        ) : null}
                        {selectedConversation ? (
                          <Chip size="small" variant="outlined" label={`Chat #${selectedConversation.id}`} />
                        ) : null}
                      </Stack>

                      <Box sx={{ borderRadius: ui.radiusCard, border: ui.panelBorder, bgcolor: ui.surfaceAlt, p: 1.25 }}>
                        <Stack spacing={1.2}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PhoneCall size={16} color={ui.metaIcon} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Telefono
                              </Typography>
                              <Typography variant="body2" noWrap>
                                {activeContactPhone || 'Non disponibile'}
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Mail size={16} color={ui.metaIcon} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Email
                              </Typography>
                              <Typography variant="body2" noWrap>
                                {activeContactEmail || 'Non disponibile'}
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <UserRound size={16} color={ui.metaIcon} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Testo libero
                              </Typography>
                              <Typography variant="body2">
                                {selectedConversation
                                  ? hasRecentInboundWindow
                                    ? 'Disponibile nelle prossime 24 ore'
                                    : 'Richiede template di riapertura'
                                  : 'Serve una conversazione selezionata'}
                              </Typography>
                            </Box>
                          </Stack>
                        </Stack>
                      </Box>

                      <Box sx={{ borderRadius: ui.radiusCard, border: ui.panelBorder, bgcolor: ui.surfaceAlt, p: 1.25 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Stato conversazione
                        </Typography>
                        <Stack spacing={0.75}>
                          <Typography variant="body2">
                            Ultimo aggiornamento: {selectedConversation?.last_message_at ? formatDateTime(selectedConversation.last_message_at) : 'Nessun dato'}
                          </Typography>
                          <Typography variant="body2">
                            Ultimo messaggio: {selectedConversation?.last_message_text || 'Nessun messaggio disponibile'}
                          </Typography>
                        </Stack>
                      </Box>

                      <Stack spacing={1}>
                        <Button
                          variant="contained"
                          startIcon={<PencilLine size={16} />}
                          onClick={() => chatTargetPhone && setComposerExpanded(true)}
                          disabled={!chatTargetPhone}
                          fullWidth
                        >
                          Apri composer
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<SendHorizontal size={16} />}
                          onClick={handleOpenTemplateModal}
                          disabled={!chatTargetPhone || !hasApprovedTemplates}
                          fullWidth
                        >
                          Template Spoki
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        minHeight: 220,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          Nessun contatto selezionato
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Seleziona una conversazione o un cliente dalla colonna sinistra per vedere i dettagli.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>
      <Dialog
        open={templateModalOpen}
        onClose={handleCloseTemplateModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Riapri conversazione</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="caption" color="text.secondary">
              Destinatario: {chatTargetPhone || 'nessun numero selezionato'}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <FormControl fullWidth size="small" disabled={loadingTemplates || sendingTemplate}>
                <InputLabel id="chat-template-select-label">Template</InputLabel>
                <Select
                  labelId="chat-template-select-label"
                  value={selectedTemplateId}
                  label="Template"
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                >
                  {approvedTemplateOptions.map((item) => (
                    <MenuItem key={item.id} value={String(item.id)}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                fullWidth
                size="small"
                disabled={!selectedTemplateLocalization || sendingTemplate || loadingTemplateDetail}
              >
                <InputLabel id="chat-template-language-label">Lingua</InputLabel>
                <Select
                  labelId="chat-template-language-label"
                  value={selectedTemplateLanguage}
                  label="Lingua"
                  onChange={(e) => setSelectedTemplateLanguage(e.target.value)}
                >
                  {templateLocalizations.map((item) => (
                    <MenuItem key={item.id || item.language_code} value={item.language_code}>
                      {languageLabel(item.language_code)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {templateModalOpen && approvedTemplateOptions.length === 0 ? (
              <Typography variant="caption" color="error.main">
                Nessun template approvato disponibile per riaprire la conversazione.
              </Typography>
            ) : null}
            {loadingTemplateDetail ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Caricamento dettaglio template...
                </Typography>
              </Stack>
            ) : null}
            {selectedTemplateLocalization ? (
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                <Chip
                  size="small"
                  variant="outlined"
                  label={languageLabel(selectedTemplateLocalization.language_code)}
                />
                {selectedTemplateStatus ? (
                  <Chip
                    size="small"
                    color={
                      ['approved', 'active'].includes(selectedTemplateStatus.toLowerCase())
                        ? 'success'
                        : ['rejected', 'paused'].includes(selectedTemplateStatus.toLowerCase())
                          ? 'error'
                          : 'warning'
                    }
                    variant="outlined"
                    label={getTemplateStatusLabel(selectedTemplateStatus)}
                  />
                ) : null}
                {selectedTemplateLocalization.header_format ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Header ${selectedTemplateLocalization.header_format}`}
                  />
                ) : null}
              </Stack>
            ) : null}
            {selectedTemplateLocalization && Object.keys(templateFields).length > 0 ? (
              <Grid container spacing={1}>
                {Object.keys(templateFields).map((field) => (
                  <Grid item xs={12} md={6} key={field}>
                    <TextField
                      fullWidth
                      size="small"
                      label={field}
                      value={templateFields[field] || ''}
                      onChange={(e) => handleTemplateFieldChange(field, e.target.value)}
                      disabled={sendingTemplate}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : null}
            {selectedTemplateLocalization?.body_text ? (
              <Box
                sx={{
                  borderRadius: ui.radiusSoft,
                  bgcolor: ui.surfaceAlt,
                  border: ui.panelBorder,
                  p: 1
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Anteprima template
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', lineHeight: 1.45 }}
                >
                  {templatePreviewBody}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateModal} disabled={sendingTemplate}>
            Annulla
          </Button>
          <Button
            variant="contained"
            startIcon={<SendHorizontal size={16} />}
            onClick={handleSendTemplate}
            disabled={!canSendTemplate || approvedTemplateOptions.length === 0}
          >
            {sendingTemplate ? 'Invio...' : 'Invia'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 0 0 1px rgba(148, 163, 184, 0.14), 0 10px 24px rgba(2, 6, 23, 0.24)'
      : '0 0 0 1px rgba(145, 158, 171, 0.12), 0 10px 22px -10px rgba(145, 158, 171, 0.2)',
  borderRadius: 8,
  overflow: 'hidden',
  backgroundImage: 'none'
}));
